/**
 * Giriş ekranı.
 *
 * Kurallar:
 *   - Hata mesajı bilgi SIZDIRMAZ: "kullanıcı yok" ile "şifre yanlış" aynı
 *     cevabı verir (sunucu tarafında da böyle — src/panel/auth.ts).
 *   - Gönderim sırasında düğme kilitlenir; çift gönderim hesabı gereksiz yere
 *     kilit sayacına yaklaştırır.
 *   - Hangi ortamda olunduğu giriş ekranında DA görünür: yanlış ortama
 *     girdiğini fatura kestikten sonra anlamak kabul edilemez.
 *
 * İki açılış bilgisi sunucudan gelir (`/api/auth/bootstrap`, oturum istemez):
 *
 *   **reCAPTCHA site anahtarı** — varsa Google'ın betiği yüklenir ve gönderimde
 *   görünmez bir belirteç üretilir. Puanı sunucu değerlendirir; tarayıcının
 *   "geçtim" demesinin bir hükmü yoktur.
 *
 *   **Otomatik doldurma** — yalnızca TEST ortamında ve `PANEL_DEV_AUTOFILL=true`
 *   iken gelir. Şifre koda gömülü DEĞİLDİR; `.env`den okunur ve canlıda bu
 *   bayrak açıksa sistem hiç başlamaz (preflight FATAL).
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogIn, ShieldCheck, UserPlus } from 'lucide-react';
import { useOturum } from '../oturum';
import { api } from '../lib/api';
import { recaptchaBelirteci, recaptchaYukle } from '../lib/recaptcha';
import { Dugme } from '../components/ui/Dugme';
import { Girdi } from '../components/ui/Girdi';
import { HataKutusu } from '../components/Durumlar';

const RECAPTCHA_EYLEMI = 'login';

/**
 * İlk yönetici formu — hiç kullanıcı yokken giriş ekranının yerini alır.
 * Sunucu sayımı kendisi yapar; bu ekran ikinci kez açılsa 409 gelir.
 */
function IlkYoneticiFormu({ canli }: { canli: boolean }) {
  const [adSoyad, setAdSoyad] = useState('');
  const [email, setEmail] = useState('');
  const [sifre, setSifre] = useState('');
  const [sifre2, setSifre2] = useState('');
  const [hata, setHata] = useState<unknown>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const uyusmuyor = sifre2 !== '' && sifre !== sifre2;

  const gonder = async (olay: FormEvent) => {
    olay.preventDefault();
    if (uyusmuyor) return;
    setHata(null);
    setBekliyor(true);
    try {
      await api.ilkYonetici({ fullName: adSoyad.trim(), email: email.trim(), password: sifre });
      // Sunucu oturum çerezini verdi; sayfa yeniden yüklenince /api/auth/me kullanıcıyı getirir.
      window.location.reload();
    } catch (yakalanan) {
      setHata(yakalanan);
      setBekliyor(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--ground)] p-4">
      <div className="kap flex w-full max-w-[46ch] flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <span
            className="flex items-center justify-center rounded-[var(--radius-lg)] bg-[var(--accent-soft)] p-3 text-[var(--accent)]"
            aria-hidden="true"
          >
            <UserPlus size={24} strokeWidth={1.75} />
          </span>
          <h1 className="text-2xl font-semibold text-[var(--ink)]">İlk yöneticiyi oluşturun</h1>
          <p className="text-sm text-[var(--ink-muted)]">
            Bu sistemde henüz kullanıcı yok. Oluşturacağınız hesap <strong>Süper Yönetici</strong> olur:
            kurulumu tamamlar, diğer kullanıcıları ekler.
          </p>
        </div>
        {canli && (
          <div className="rounded-[var(--radius-md)] bg-[var(--live-warning)] px-3 py-2 text-center text-xs font-semibold text-[var(--live-warning-ink)]">
            CANLI ORTAM — kesilen faturalar gerçektir
          </div>
        )}
        <form
          onSubmit={(o) => void gonder(o)}
          className="flex flex-col gap-4 rounded-[var(--radius-lg)] bg-[var(--surface)] p-4 shadow-[var(--shadow-uzuv)]"
        >
          <Girdi
            etiket="Ad soyad"
            required
            minLength={2}
            autoFocus
            value={adSoyad}
            onChange={(o) => setAdSoyad(o.target.value)}
          />
          <Girdi
            etiket="E-posta"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(o) => setEmail(o.target.value)}
          />
          <Girdi
            etiket="Şifre"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={sifre}
            onChange={(o) => setSifre(o.target.value)}
            ipucu="En az 8 karakter; büyük/küçük harf, rakam ve işaret karışık."
          />
          <Girdi
            etiket="Şifre (tekrar)"
            type="password"
            autoComplete="new-password"
            required
            value={sifre2}
            onChange={(o) => setSifre2(o.target.value)}
            {...(uyusmuyor ? { hata: 'Şifreler aynı değil.' } : {})}
          />
          {hata !== null && <HataKutusu hata={hata} />}
          <Dugme
            type="submit"
            gorunum="birincil"
            bekliyor={bekliyor}
            disabled={uyusmuyor}
            ikon={<UserPlus size={15} />}
            className="w-full"
          >
            Hesabı oluştur ve giriş yap
          </Dugme>
          <p className="text-xs text-[var(--ink-faint)]">
            Bu ekran yalnızca ilk kurulumda görünür; hesap oluşunca bir daha açılmaz.
          </p>
        </form>
      </div>
    </div>
  );
}

export function Giris() {
  const { girisYap } = useOturum();
  const [email, setEmail] = useState('');
  const [sifre, setSifre] = useState('');
  const [hata, setHata] = useState<unknown>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const [otomatikDolduruldu, setOtomatikDolduruldu] = useState(false);

  const { data: acilis } = useQuery({
    queryKey: ['acilis'],
    queryFn: api.acilis,
    // Açılış bilgisi süreç boyunca değişmez; tekrar tekrar sorulmaz.
    staleTime: Infinity,
    retry: 1,
  });

  const siteKey = acilis?.recaptchaSiteKey ?? null;

  // reCAPTCHA betiğini ÖNDEN yükle: gönderim anında beklemek girişi yavaşlatır.
  useEffect(() => {
    if (!siteKey) return;
    // Hata yutulur — betik yüklenmezse sunucu kendi kuralına göre karar verir.
    void recaptchaYukle(siteKey).catch(() => undefined);
  }, [siteKey]);

  // Test ortamında alanları doldur — kullanıcı her seferinde şifre yazmasın.
  useEffect(() => {
    if (!acilis?.autofill || otomatikDolduruldu) return;
    setEmail(acilis.autofill.email);
    setSifre(acilis.autofill.password);
    setOtomatikDolduruldu(true);
  }, [acilis, otomatikDolduruldu]);

  const gonder = async (olay: FormEvent) => {
    olay.preventDefault();
    setHata(null);
    setBekliyor(true);
    try {
      const belirtec = siteKey ? await recaptchaBelirteci(siteKey, RECAPTCHA_EYLEMI) : null;
      await girisYap(email, sifre, belirtec);
    } catch (yakalanan) {
      setHata(yakalanan);
      // Şifre alanı temizlenir: yanlış şifre ekranda durmasın.
      setSifre('');
      setOtomatikDolduruldu(true); // tekrar doldurup aynı hatayı üretmesin
    } finally {
      setBekliyor(false);
    }
  };

  // Temiz kurulum: giriş yapacak kimse yok — ilk yönetici formu.
  if (acilis?.ilkKurulum) return <IlkYoneticiFormu canli={acilis.isLive} />;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--ground)] p-4">
      {/* Genişlik sabit px DEĞİL: okunabilirlik sınırı olarak ch kullanılır. */}
      <div className="kap flex w-full max-w-[46ch] flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <span
            className="flex items-center justify-center rounded-[var(--radius-lg)] bg-[var(--accent-soft)] p-3 text-[var(--accent)]"
            aria-hidden="true"
          >
            <ShieldCheck size={24} strokeWidth={1.75} />
          </span>
          <h1 className="text-2xl font-semibold text-[var(--ink)]">Deercase Fatura</h1>
          <p className="text-sm text-[var(--ink-muted)]">
            Shopify siparişlerinden kesilen e-Arşiv ve e-Fatura belgelerini buradan izlersiniz.
          </p>
        </div>

        {/* Ortam şeridi giriş ekranında da var: yanlış ortama girmek pahalı. */}
        {acilis && (
          <div
            className={
              acilis.isLive
                ? 'rounded-[var(--radius-md)] bg-[var(--live-warning)] px-3 py-2 text-center text-xs font-semibold text-[var(--live-warning-ink)]'
                : 'rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface-sunken)] px-3 py-2 text-center text-xs font-medium text-[var(--ink-muted)]'
            }
          >
            {acilis.isLive
              ? 'CANLI ORTAM — kesilen faturalar gerçektir'
              : 'TEST ORTAMI — kesilen faturalar gerçek değildir'}
          </div>
        )}

        <form
          onSubmit={(olay) => void gonder(olay)}
          className="flex flex-col gap-4 rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] p-4 shadow-[var(--shadow-uzuv)]"
        >
          <Girdi
            etiket="E-posta"
            type="email"
            name="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(olay) => setEmail(olay.target.value)}
            placeholder="ad@deercase.com"
          />
          <Girdi
            etiket="Şifre"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={sifre}
            onChange={(olay) => setSifre(olay.target.value)}
          />

          {acilis?.autofill && (
            <div className="flex flex-col gap-1.5 rounded-[var(--radius-sm)] bg-[var(--state-wait-soft)] px-3 py-2 text-xs text-[var(--state-wait)]">
              <span>Test ortamı — giriş bilgileri (yalnızca test sunucusunda görünür):</span>
              <span className="rakam text-[var(--ink)] select-all">
                {acilis.autofill.email} · {acilis.autofill.password}
              </span>
              {(email !== acilis.autofill.email || sifre !== acilis.autofill.password) && (
                <button
                  type="button"
                  className="self-start font-medium underline underline-offset-2 hover:text-[var(--ink)]"
                  onClick={() => {
                    setEmail(acilis.autofill!.email);
                    setSifre(acilis.autofill!.password);
                    setHata(null);
                  }}
                >
                  Alanlara doldur
                </button>
              )}
            </div>
          )}

          {hata !== null && <HataKutusu hata={hata} />}

          <Dugme
            type="submit"
            gorunum="birincil"
            bekliyor={bekliyor}
            ikon={<LogIn size={15} />}
            className="w-full"
          >
            {bekliyor ? 'Giriş yapılıyor…' : 'Giriş yap'}
          </Dugme>

          <p className="text-xs text-[var(--ink-faint)]">
            Beş hatalı denemeden sonra hesap 15 dakika kilitlenir. Şifrenizi bilmiyorsanız yöneticiden yeni
            şifre isteyin.
          </p>

          {/*
            Google'ın marka şartı: reCAPTCHA rozeti gizlenirse bu bildirim
            görünür olmak ZORUNDADIR. Rozeti gizlemesek de bildirim kalsın —
            kullanıcı hangi verinin nereye gittiğini bilmeli.
          */}
          {siteKey && (
            <p className="border-t border-[var(--line)] pt-3 text-xs text-[var(--ink-faint)]">
              Bu ekran Google reCAPTCHA ile korunuyor.{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-[var(--ink-muted)]"
              >
                Gizlilik
              </a>{' '}
              ve{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-[var(--ink-muted)]"
              >
                Şartlar
              </a>
              .
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
